import { faPortrait } from '@fortawesome/free-solid-svg-icons';
import { Button } from 'primereact/button';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import {
  getAssignmentAndReceptionStatusSeveritySound,
  getWarehouseCourierOrderAssignmentStatusConfig,
} from '../../../../../configs/orders';
import ToastContext from '../../../../../context/ToastContext';
import { IntBool } from '../../../../../enums/booleans';
import { FileTypes } from '../../../../../enums/files';
import { WarehouseAssignmentAndReceptionStatusSeverity } from '../../../../../enums/orders';
import { UserRole } from '../../../../../enums/users';
import useAxiosHook from '../../../../../hooks/useAxiosHook';
import { useEndpointGuard } from '../../../../../hooks/useEndpointGuard';
import usePageTitle from '../../../../../hooks/usePageTitle';
import usePreventWindowClose from '../../../../../hooks/usePreventWindowClose';
import usePrevious from '../../../../../hooks/usePrevious';
import useRouteDialog from '../../../../../hooks/useRouteDialog';
import useTableColumns from '../../../../../hooks/useTableColumns';
import useTableState from '../../../../../hooks/useTableState';
import useToastMessage from '../../../../../hooks/useToastMessage';
import {
  AvailableCouriersCollection,
  AvailableCouriersCollectionQueryParams,
} from '../../../../../types/api/couriers';
import {
  CourierAssignmentReportQueryParams,
  CourierDeliveryAssignmentCollection,
  CourierDeliveryAssignmentCollectionQueryParams,
  CourierSummaryCollection,
  CourierSummaryCollectionQueryParams,
  DeleteCourierAssignment,
  UndoAssignedShipmentQueryParams,
  UpdateCourierDeliveryAssignment,
  UpdateCourierDeliveryAssignmentRequestPayload,
} from '../../../../../types/api/orders';
import { EntityIdRouteParams } from '../../../../../types/routing';
import { Unpacked } from '../../../../../types/util';
import {
  assignShipmentGuard,
  cancelShipmentAssignmentGuard,
} from '../../../../../utils/constants/auth/courierShipmentAssignment';
import {
  RoutePaths,
  constructIdRoute,
} from '../../../../../utils/constants/routePaths';
import { playAudio } from '../../../../../utils/helpers/audio';
import { downloadFile, getFileName } from '../../../../../utils/helpers/files';
import { sequential } from '../../../../../utils/helpers/functions';
import { queryString } from '../../../../../utils/helpers/http';
import { tryInt } from '../../../../../utils/helpers/parse';
import {
  errorToast,
  successToast,
  warnToast,
} from '../../../../../utils/helpers/primereact';
import Table from '../../../../DataTable/Table/Table';
import Flex from '../../../../layout/flex/Flex';
import MainContent from '../../../../layout/flex/MainContent';
import DetailsSection from '../../../Components/DetailsSection/DetailsSection';
import HeaderPages from '../../../Components/HeaderPages/HeaderPages';
import ViewActiveOrderDialog from '../../../Orders/Dialogs/View/ViewActiveOrderDialog';
import { SingleOrder } from '../../../Orders/Orders.functions';
import CommonStateContext from '../../_AssignmentAndReception/CommonStateContext';
import CreateWorkOrderDialog from '../../_AssignmentAndReception/Dialogs/Create/WorkOrder/CreateWorkOrderDialog';
import Barcode from '../../_AssignmentAndReception/Sidebar/Barcode';
import Filters from '../../_AssignmentAndReception/Sidebar/Filters';
import {
  CandidatesFilter,
  WarehousePage,
} from '../../_AssignmentAndReception/types';
import useCommonState from '../../_AssignmentAndReception/useCommonState';
import {
  additionalColumnProperties,
  generateContextMenu,
  getColumnHeadersMap,
  tableStorageKey,
} from './CourierShipmentAssignment.functions';
import Stats from './Sidebar/Stats';

function CourierShipmentAssignment(): JSX.Element {
  const { t } = useTranslation();

  usePageTitle(t('Courier Shipment Delivery Assignment'));

  // Prevent entire page being closed unintentionally.
  usePreventWindowClose();

  const { toastRef, bottomRightToastRef } = useContext(ToastContext);

  const assignGuard = useEndpointGuard(assignShipmentGuard);
  const cancelGuard = useEndpointGuard(cancelShipmentAssignmentGuard);

  const columnHeadersMap = useMemo(() => getColumnHeadersMap(t), [t]);

  const state = useCommonState({
    page: WarehousePage.CourierShipmentAssignment,
  });
  const {
    courierFilter,
    candidatesFilter,
    debouncedSerialNumberFilter,
    isCreateWorkOrderDialogShown,
    handleCreateWorkOrderDialogSuccess,
    handleCreateWorkOrderDialogHide,
    isDateValid,
    courierWorkOrderId,
    hasCourierWorkOrder,
    rowClassName,
    nativeHubId,
  } = state;

  const {
    page,
    limit,
    tableRef,
    sortField,
    sortOrder,
    selection,
    setPage,
    setLimit,
    setSortField,
    setSortOrder,
    setSelection,
  } =
    useTableState<Unpacked<CourierDeliveryAssignmentCollection>>(
      tableStorageKey
    );

  const { columns, columnOptions, selectedColumns, setSelectedColumns } =
    useTableColumns(
      page,
      limit,
      'warehouse_courierShipmentAssignment',
      columnHeadersMap,
      columnHeadersMap,
      additionalColumnProperties(t)
    );
  console.log('courierWorkOrderId courierWorkOrderId ', courierWorkOrderId);
  const { data, error, isLoading, reload } =
    useAxiosHook<CourierDeliveryAssignmentCollection>(
      '/orders/courier/assignments/delivery' +
        queryString<CourierDeliveryAssignmentCollectionQueryParams>({
          work_order_id: courierWorkOrderId ?? '',
          show_candidates:
            isDateValid && candidatesFilter === CandidatesFilter.Hidden ? 0 : 1,
          sort_is_candidate:
            candidatesFilter === CandidatesFilter.Last ? 'asc' : 'desc',
          serial_number: debouncedSerialNumberFilter,
          page: page,
          limit: limit,
        }),
      { skipWhen: !hasCourierWorkOrder }
    );

  const couriersCollectionRequest = useAxiosHook<AvailableCouriersCollection>(
    '/couriers/available' +
      queryString<AvailableCouriersCollectionQueryParams>({
        is_assigned_to_region: IntBool.True,
        user_role_id: UserRole.Courier,
      })
  );

  const statsRequest = useAxiosHook<CourierSummaryCollection>(
    '/orders/courier/summary' +
      queryString<CourierSummaryCollectionQueryParams>({
        work_order_id: courierWorkOrderId ?? '',
        hub_id: nativeHubId!,
      }),
    { skipWhen: !hasCourierWorkOrder }
  );

  useToastMessage(undefined, statsRequest.error, {
    error: {
      summary: t('An error occured while reading stats data.'),
    },
  });

  const {
    data: assignOrderData,
    error: assignOrderError,
    reload: assignOrderReload,
  } = useAxiosHook<UpdateCourierDeliveryAssignment>();

  const handleValidBarcodeScan = useCallback(
    (barcode: string) => {
      if (!barcode.length) {
        return;
      }

      const data: UpdateCourierDeliveryAssignmentRequestPayload = {
        work_order_id: courierWorkOrderId!,
        serial_number: barcode,
      };

      assignOrderReload({
        url: '/orders/courier/assignments/delivery',
        method: 'PUT',
        data,
      });
    },
    [assignOrderReload, courierWorkOrderId]
  );

  const prevAssignOrderData = usePrevious(assignOrderData);

  useToastMessage(undefined, assignOrderError, {
    error: {
      summary: t('An error occured while trying to assign shipment.'),
    },
  });

  useEffect(() => {
    if (!assignOrderData || assignOrderData === prevAssignOrderData) {
      return;
    }

    const warehouseOrderAssignmentStatusConfig =
      getWarehouseCourierOrderAssignmentStatusConfig(t);

    const status = assignOrderData.assignment_status_id;

    const config = warehouseOrderAssignmentStatusConfig[status];

    if (!config) {
      errorToast(toastRef, t('Error'), t('An unexpected error occurred.'));
      return;
    }

    switch (config.severity) {
      case WarehouseAssignmentAndReceptionStatusSeverity.Info:
        successToast(toastRef, t('Success'), config.message);
        break;

      case WarehouseAssignmentAndReceptionStatusSeverity.Warning:
        warnToast(toastRef, t('Warning'), config.message);
        break;

      case WarehouseAssignmentAndReceptionStatusSeverity.Error:
        errorToast(toastRef, t('Error'), config.message);
        break;
    }

    const assignmentStatusSound = getAssignmentAndReceptionStatusSeveritySound(
      config.severity,
      true
    );

    if (assignmentStatusSound) {
      playAudio(assignmentStatusSound);
    }

    if (
      config.severity !== WarehouseAssignmentAndReceptionStatusSeverity.Error
    ) {
      reload();
      statsRequest.reload();
    }
  }, [assignOrderData, prevAssignOrderData, reload, statsRequest, t, toastRef]);

  const { id } = useParams<EntityIdRouteParams>();

  const {
    show: showViewOrderDialog,
    hide: hideViewOrderDialog,
    isVisible: isViewOrderDialogVisible,
  } = useRouteDialog(
    RoutePaths.CourierShipmentAssignments,
    constructIdRoute(
      RoutePaths.ViewCourierShipmentAssignment,
      id ?? selection?.order_id
    )
  );

  const { data: orderData, isLoading: isOrderDataLoading } =
    useAxiosHook<SingleOrder>(
      {
        url: `/orders/${id}`,
      },
      {
        skipWhen: !isViewOrderDialogVisible,
      }
    );

  const {
    data: cancelShipmentData,
    error: cancelShipmentError,
    reload: cancelShipmentReload,
  } = useAxiosHook<DeleteCourierAssignment>();

  useToastMessage(cancelShipmentData, cancelShipmentError, {
    success: {
      summary: t('Successfully cancelled assignment for shipment {{id}}.', {
        id: cancelShipmentData?.serial_number ?? '',
      }),
      callback: () => sequential(reload, statsRequest.reload),
    },
    error: {
      summary: t(
        'An error occured while trying to cancel the shipment assignment.'
      ),
    },
  });

  const assignShipment = useCallback(() => {
    if (!selection?.serial_number) {
      return;
    }

    handleValidBarcodeScan(selection.serial_number);
  }, [handleValidBarcodeScan, selection?.serial_number]);

  function handlePrintCourierReceiptBtnClick() {
    if (!hasCourierWorkOrder) {
      return;
    }

    const hubId = couriersCollectionRequest.data?.find(
      (c) => c.id === courierFilter
    )?.hub_id;

    if (!hubId) {
      return;
    }

    downloadFile(
      `${process.env.REACT_APP_REPORT_URL}/orders/courier/assignments/pdf` +
        queryString<CourierAssignmentReportQueryParams>({
          work_order_id: courierWorkOrderId!,
          hub_id: String(hubId),
        }),
      getFileName(
        t('CourierShipmentDeliveryAssignment'),
        String(courierWorkOrderId),
        true
      ),
      FileTypes.PDF,
      bottomRightToastRef
    );
  }

  const contextMenuModel = useMemo(() => {
    function handleCancelCMClick() {
      if (!selection) {
        return;
      }

      cancelShipmentReload({
        url:
          `/orders/courier/assignments/delivery/${selection?.order_id}` +
          queryString<UndoAssignedShipmentQueryParams>({
            work_order_id: courierWorkOrderId!,
          }),
        method: 'DELETE',
      });
    }

    const isAssignShown =
      isDateValid && assignGuard && !!tryInt(selection?.is_candidate);

    const isCancelShown =
      isDateValid &&
      cancelGuard &&
      !!selection?.is_candidate &&
      !parseInt(selection.is_candidate);

    return generateContextMenu({
      t,
      onViewClick: showViewOrderDialog,
      isAssignShown,
      onAssignClick: assignShipment,
      isCancelShown,
      onCancelClick: handleCancelCMClick,
    });
  }, [
    assignGuard,
    assignShipment,
    cancelGuard,
    cancelShipmentReload,
    courierWorkOrderId,
    isDateValid,
    selection,
    showViewOrderDialog,
    t,
  ]);

  return (
    <CommonStateContext.Provider value={state}>
      <div className="page page-courier-assignment">
        <HeaderPages
          title={t('Assignment To Courier')}
          subtitle={t('Assign orders to a courier for delivery')}
          icon={faPortrait}
        >
          <Button
            label={t('Print load Courier Receipt')}
            icon="fas fa-print"
            className="main-btn"
            disabled={!hasCourierWorkOrder || !courierFilter}
            onClick={handlePrintCourierReceiptBtnClick}
          />
        </HeaderPages>

        <ViewActiveOrderDialog
          data={orderData}
          visible={isViewOrderDialogVisible}
          onHide={hideViewOrderDialog}
          isLoading={isOrderDataLoading}
        />

        <CreateWorkOrderDialog
          visible={isCreateWorkOrderDialogShown}
          onHide={handleCreateWorkOrderDialogHide}
          onSuccess={handleCreateWorkOrderDialogSuccess}
          couriers={couriersCollectionRequest.data}
          courierFilter={courierFilter}
        />

        <Flex direction="column">
          <Filters availableCouriersRequest={couriersCollectionRequest} />
          <DetailsSection>
            {assignGuard && (
              <Barcode onValidBarcodeScan={handleValidBarcodeScan} />
            )}

            <Stats request={statsRequest} />
          </DetailsSection>

          <MainContent>
            <Table
              tableRef={tableRef}
              columns={columns}
              data={hasCourierWorkOrder ? data : undefined}
              isLoading={isLoading}
              hasError={!!error}
              filterHeight={360}
              reload={() => sequential(reload, statsRequest.reload)}
              isReloadDisabled={!hasCourierWorkOrder}
              setPage={setPage}
              setLimit={setLimit}
              sortField={sortField}
              rows={limit}
              setSortField={setSortField}
              setSortOrder={setSortOrder}
              setSelection={setSelection}
              sortOrder={sortOrder}
              selection={selection}
              storageString={tableStorageKey}
              selectedColumns={selectedColumns}
              setSelectedColumns={setSelectedColumns}
              columnOptions={columnOptions}
              headerTitle={t('Shipments')}
              onRowDoubleClick={showViewOrderDialog}
              contextMenuModel={contextMenuModel}
              rowClassName={rowClassName}
            />
          </MainContent>
        </Flex>
      </div>
    </CommonStateContext.Provider>
  );
}

export default CourierShipmentAssignment;
